import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  AGENT_MESSAGE_MAX_CHARS,
  exportFilename,
  FILE_MAX_BYTES,
  MCP_FLAG,
  AGENT_IMAGE_EXTENSIONS,
  TABLE_EXTENSIONS,
  type AgentConversation,
  type AgentConversationFile,
  type AgentDataBlock,
  type AgentExportOffer,
  type AgentLibraryFile,
} from '@forecastify/shared';
import {
  Badge,
  Button,
  DropdownMenu,
  Icon,
  Textarea,
  Toaster,
  TruncatedText,
  type MenuItem,
  useIsMobile,
} from '@forecastify/ui';
import mark from '../../assets/forecastify-mark.svg';
import { useI18n, useT, type Translate } from '../../i18n';
import { useFileDrop } from '../file-drop';
import { saveTextFile } from '../file-helpers';
import { ListHeader, usePageHeader } from '../page-header';
import { useSidebarContext } from '../sidebar/use-sidebar';
import { useSession } from '../../session';
import { greetingKey } from './greeting';
import { useFollowThread } from './follow-thread';
import { AgentFileChips, AgentFileLibrary, AgentPendingFiles } from './agent-files';
import { AgentDataCard } from './agent-data-card';
import { AgentMarkdown } from './agent-markdown';
import { AgentProse } from './agent-prose';
import { ThinkingIndicator } from './thinking-orb';
import { AgentProposalCard, type ThreadProposal } from './agent-proposal-card';
/* ⚠️ C48 · the domain's name in the reader's language, from the dialog that owns the six of them —
 * a second mapping here would be a second place for *"időbejegyzések"* to drift. */
import { domainLabel } from '../exports/export-dialog';
/* C53 · the marks of the clients the setup prompt is for. `provider-logo.tsx` argues the shape:
 * `<img>` with a bundled URL rather than inlined markup, so Vite hashes and caches each file and a
 * vendor's artwork stays a file we can replace when they redraw it. */
import claudeMark from '../../assets/claude-icon.svg';
import openaiMark from '../../assets/openai-icon.svg';
import perplexityMark from '../../assets/perplexity-icon.svg';
import geminiMark from '../../assets/google-gemini-icon.svg';
import { ComposerGlow } from './composer-glow';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The Agent. C17 drew the shell; C27 fills it in.                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **C17'S DECISION IS NOT REVERSED BY THIS FILE — IT IS SATISFIED.** The product owner's note of
 * 25 Aug 2026 was that *"a canned agent fabricates answers, which is the one thing this rebuild
 * refuses everywhere else"*, and the screen therefore shipped as a shell. What has changed is that
 * there is now a real model behind the composer. What has **not** changed is the rule: nothing on
 * this screen may state a fact about the workspace that a server did not produce.
 *
 * In C27.1 that rule was kept the strongest way available — the agent had no tools at all, and
 * `preamble.ts` told it so in the most direct language in the codebase. **C27.2 replaces the
 * prohibition with the mechanism**: a figure on this screen arrives in a `data` frame that a TOOL
 * emitted, rendered by the product's own list component, and the model never authors a row.
 * `agent.readOnly` is the standing notice that says as much to the person — and it replaced
 * `agent.noData`, which the tools made false.
 *
 * ⚠️ **`agent.notConnected` — C17's own sentence — is still here and still true.** It is now the
 * `agent_not_configured` branch: a deployment with no `ANTHROPIC_API_KEY` renders exactly the screen
 * C17 built, notice and all. The shell was not thrown away; it became the off state.
 *
 * ⚠️ **PRESENTATIONAL. Every piece of state arrives as a prop.** `agent-route.tsx` owns the turn, the
 * stream and the failure. This file owns the mockup's geometry, and the reason for the split is that
 * the geometry is the part with measured numbers in it: 620px for the empty state, 680px for the
 * thread, `text-[26px]` for the greeting, `text-[13.5px]` for a bubble — `markup.html` 1500–1575.
 *
 * ⚠️ **Three of the mockup's controls are still deliberately absent**, for the reasons C17 gave and
 * which C27.1 does not change: the history dropdown and *Új beszélgetés* arrive with C27.4 (there is
 * a table now, but no menu yet), and file attachment stays out — the mockup collects filenames and
 * uploads nothing, and C22's object store is not what makes an agent useful.
 */

/**
 * One tool call, as a row in the transcript.
 *
 * ⚠️⚠️ **THIS ROW IS WHAT MAKES AN ANSWER CHECKABLE, AND IT IS THE REASON THE MODEL IS TOLD TO MAKE
 * ITS READS SEPARATELY AND VISIBLY** (`transport.ts` picks `effort: 'high'` partly for this). Somebody
 * reading *"kettő csúszik"* can see that a task search ran and returned two rows. Without the row,
 * the prose and the table would be the only evidence and there would be no way to tell a read from a
 * recollection.
 */
export interface ThreadStep {
  readonly id: string;
  /** ⚠️ The tool's machine NAME. This component looks up the label — no frame carries prose. */
  readonly name: string;
  readonly state: 'running' | 'done' | 'failed';
  /** ⚠️ Null for a tool that answers no list: *"0 feladat"* under an aggregate says nothing. */
  readonly count: number | null;
  /**
   * Which refusal, when it was refused.
   *
   * ⚠️ **A code, never a sentence** — rule 5, and no frame carries prose. It is here because one
   * refusal reads completely differently from the others: `one_at_a_time` means *"the model prepared
   * one write and it is waiting for you"*, which is a rule rather than a fault.
   */
  readonly errorCode: string | null;
}

/** One turn as the screen renders it — not the wire shape. `agent-route.tsx` builds these. */
export interface ThreadTurn {
  readonly id: string;
  readonly role: 'user' | 'assistant';
  readonly text: string;
  /** The tool calls this turn made, in order. Empty for a turn that answered from the prompt. */
  readonly steps: readonly ThreadStep[];
  /**
   * The structured results, in the order they arrived.
   *
   * ⚠️⚠️ **NOTHING RENDERS A CARD THIS ARRAY DOES NOT HOLD, AND NOTHING PUTS ONE IN IT EXCEPT A
   * `data` FRAME.** That is C17's property carried into C27.2: the figures on screen came out of a
   * service, not out of a sentence. `__tests__/agent.test.tsx` asserts it from the other side.
   */
  readonly blocks: readonly AgentDataBlock[];
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C48 · THE EXPORTS THIS TURN OFFERED. A FILTER TO RUN, NOT A FILE │
   * │ AND NOT A TABLE. R-34.                                                │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * ⚠️ **An array where `proposal` is a field**, and the asymmetry is the server's: a proposal ends
   * the turn at the first one (R-25), while an export offer is an ordinary read result — the model
   * can set up an hours export and a tasks export in one answer, and two controls with two different
   * filters is a legitimate reply.
   *
   * ⚠️⚠️ **NOT GOVERNED BY `data-cards.ts`.** That flag switches off *"the tables that follow a
   * read"*; an offer is a control, in the same category as the confirmation card the flag also
   * leaves alone. `agent.service.ts` argues it at the emission site.
   */
  readonly offers: readonly AgentExportOffer[];
  /**
   * The write this turn offered, if it offered one.
   *
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ONE PER TURN, BECAUSE THE SERVER ENDS THE TURN AT THE FIRST ONE.  │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * R-25: a write tool proposes and the loop stops. A second proposal in the same round is refused
   * server-side, so this is a field rather than an array — two cards with two buttons is somebody
   * accepting one and believing they accepted both.
   */
  readonly proposal: ThreadProposal | null;
  /**
   * The summarised reasoning, when there is any.
   *
   * ⚠️ **Null and empty string are different**, and the API makes that distinction real: no thinking
   * block at all is `null`, and a thinking block whose text is empty is the `display: 'omitted'`
   * trap `transport.ts` guards against. Both render as no disclosure; only the second is a bug.
   */
  readonly thinking: string | null;
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THIS QUESTION GOT NO ANSWER, AND IT SAYS SO IN RED.              │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * Only ever true on a `user` turn. `beginTurn` persists a question **before** the model is called,
   * deliberately, so that a third party's failure does not eat what somebody typed — and the cost of
   * that decision is a thread in which the question is present and nothing follows it. Two of them in
   * a row read exactly like an app that lost the message.
   *
   * ⚠️ **It does not say WHY, because nothing knows.** The refusal code is not stored: the live
   * notice under the composer names it while it is fresh, and this is what survives a reload.
   * `agent-route.tsx` sets it from the `error` frame and derives it on load.
   */
  readonly failed: boolean;
  /**
   * 29 Oct 2026 · the thread before this answer was summarised while it was written, so the model
   * knows what came earlier only from that summary. The bubble says so in one quiet line. Always
   * false on a person's own turn.
   */
  readonly compacted: boolean;
}

/** Which stage of a turn is in flight, so the composer and the indicator agree. */
export type AgentPhase =
  /** Nothing in flight. */
  | 'idle'
  /** Sent; the model has not started answering. ⚠️ Its own phase — see `Working`. */
  | 'sending'
  /** A thinking block is open. */
  | 'thinking'
  /** Text is arriving. */
  | 'answering';

export interface AgentScreenProps {
  /**
   * Which address is showing this screen, as its `data-screen`.
   *
   * ⚠️⚠️ **A PROP RATHER THAN A CONSTANT, BECAUSE ONE SCREEN SERVES TWO ROUTES.** `/agent` is a new
   * conversation and `/agent/:conversationId` is a particular one; `routes.tsx` gives them distinct
   * markers so the reachability gate can tell them apart, and a shared one would make both cases
   * pass for the wrong reason. The two literals rather than `string`, so a typo does not compile.
   *
   * ⚠️ **Both of this component's two returns carry it** — the mockup's empty state and the thread
   * are different elements, and a marker on only one of them is the *"rendered fine and was checked
   * by nothing"* failure CLAUDE.md §4 names, on whichever branch was missed.
   */
  readonly marker: 'agent' | 'agent-conversation';
  readonly turns: readonly ThreadTurn[];
  readonly phase: AgentPhase;
  /**
   * Why the composer cannot be used at all, if it cannot.
   *
   * ⚠️ **A catalogue key rather than a sentence**, so this component holds no user-facing string it
   * did not look up — rule 7. `agent-route.tsx` maps a refusal `code` to the key.
   */
  readonly blocked: 'notConnected' | 'notInPlan' | null;
  /** A refusal from the last turn, as a catalogue key. Cleared when the next one is sent. */
  readonly failure: string | null;
  readonly onSend: (text: string) => void;
  /** ⚠️ Stops the turn. See the note on the button — this is a real request, not a UI reset. */
  readonly onStop: () => void;
  /**
   * C43 · attach files to this conversation, now rather than on send.
   *
   * ⚠️ **The route owns the upload**, because a brand-new thread has no conversation to attach to
   * yet and creating one is the route's job — the same sequencing `onSend` already has.
   *
   * ⚠️⚠️ **A LIST SINCE C48-1, AND THE DROP IS WHY.** A picker can be held to one file; a drop
   * cannot — somebody selects three months of exports in Finder and lets go of all three. The route
   * uploads them one at a time (the endpoint takes one file) and decides which are acceptable,
   * because it is also where the SERVER's refusal for the same reason is turned into a sentence.
   */
  readonly onAttach: (files: readonly File[]) => void;
  /** ⚠️ One batch at a time: each upload is scanned, and the button says so rather than queueing. */
  readonly attaching: boolean;
  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C47 · THIS THREAD'S FILES, AND THE SCREEN PARTITIONS THEM.          │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * One list, three surfaces: `messageId === null` is the strip above the composer, the rest are
   * chips above the message each one names, and the panel's first section is all of them. C43 held
   * neither the list nor a conversation id here — `Attachments` fetched its own — and
   * `agent-route.tsx` argues why that stopped working once there was more than one surface.
   */
  readonly files: readonly AgentConversationFile[];
  /** Every file this member has handed the Agent, in any thread. Loaded when the panel opens. */
  readonly library: readonly AgentLibraryFile[];
  readonly libraryOpen: boolean;
  readonly libraryLoading: boolean;
  readonly onShowLibrary: (open: boolean) => void;
  /** Send a file the member already has with the next question. Takes a link id (R-25's shape). */
  readonly onAttachExisting: (fileId: string) => void;
  /** ⚠️ Only ever called for a PENDING file — `agent-route.tsx` argues why a sent one has no ×. */
  readonly onRemoveFile: (id: string) => void;
  /**
   * Open a task from a data card, by KEY.
   *
   * ⚠️ **A key rather than an id, because `/tasks/:key` is the address** — `HSO-131` is what people
   * paste out of a commit message, and a uuid would be a link nobody can read. `routes.tsx` argues it
   * where the route is declared.
   */
  readonly onOpenTask: (key: string) => void;
  /**
   * ⚠️ **C48 · the person runs the export, not the screen and not the model.** The route hands this
   * to `useExport().open()`, which opens the product's own dialog pre-filled — so what this screen
   * does with an offer is exactly what it does with a task key: hand it to whoever owns that surface.
   */
  readonly onExport: (offer: AgentExportOffer) => void;
  /**
   * Confirm the proposed write.
   *
   * ⚠️ **The id, and nothing else — there is no payload anywhere on this path.** R-25: the server
   * executes the input **it** stored when the proposal was made. A handler that carried the values
   * back would be the general-purpose write endpoint that decision exists to refuse.
   */
  readonly onAcceptProposal: (id: string) => void;
  readonly onDeclineProposal: (id: string) => void;
  /** ⚠️ C46 · a correction re-proposes; `agent-proposal-card.tsx` argues why it is not an accept. */
  readonly onReviseProposal: (id: string, values: Readonly<Record<string, string | null>>) => void;

  /* ── C27.4 · the history menu, which C17 declined for having no table behind it ────────────── */

  /**
   * This member's conversations, most recently used first.
   *
   * ⚠️ **Newest first is the SERVER's order** (`updated_at desc`) and this component does not re-sort:
   * a thread you replied to today is today's thread, and a second ordering here would be a second
   * answer to what *recent* means.
   */
  readonly conversations: readonly AgentConversation[];
  /** Until the first read settles: the menu says it is loading rather than that there are none. */
  readonly conversationsLoading?: boolean;
  /** Which one is open, or `null` for a thread that has not been created yet. */
  readonly openId: string | null;
  readonly onOpen: (id: string) => void;
  readonly onNew: () => void;
  /**
   * ⚠️ **Deletes the OPEN conversation, which is why it takes no id.** A destructive row per thread
   * inside a menu of threads is a mis-click waiting to happen — every other row in that menu opens
   * something, and the one that destroys it would be a pixel away.
   */
  readonly onDelete: () => void;
  /**
   * 29 Oct 2026 · renames the OPEN conversation, for `onDelete`'s reason — the row sits beside it in
   * the same menu and acts on the same thread. Called with the trimmed, changed name.
   */
  readonly onRename: (title: string) => void;
}

export function AgentScreen({
  marker,
  turns,
  phase,
  blocked,
  failure,
  onSend,
  onAttach,
  attaching,
  files,
  library,
  libraryOpen,
  libraryLoading,
  onShowLibrary,
  onAttachExisting,
  onRemoveFile,
  onStop,
  onOpenTask,
  onExport,
  onAcceptProposal,
  onDeclineProposal,
  onReviseProposal,
  conversations,
  conversationsLoading,
  openId,
  onOpen,
  onNew,
  onDelete,
  onRename,
}: AgentScreenProps): ReactNode {
  const t = useT();
  const session = useSession();
  const [draft, setDraft] = useState('');
  const busy = phase !== 'idle';

  /**
   * ┌──────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ ONE LINE PER CHAT, PICKED WHEN THE CHAT STARTS.                       │
   * └──────────────────────────────────────────────────────────────────┘
   *
   * ⚠️⚠️ **NOT PER RENDER, WHICH IS THE WHOLE REASON THIS IS STATE AND NOT A CALL IN THE
   * BODY.** `Math.random()` where the heading is built would re-roll on every keystroke, so the
   * greeting would flicker through three variants while somebody typed their question. The product
   * owner asked for a fresh line *"whenever a new chat is started"*, and that is exactly when this
   * re-rolls: the empty state is the new chat.
   *
   * ⚠️ **Derived during render rather than in an effect** — the same pattern `app-shell.tsx`'s
   * `NavGroup` uses to reopen itself. An effect would paint one line and then replace it.
   */
  const empty = turns.length === 0 && phase === 'idle';
  const [pick, setPick] = useState(() => Math.random() * 1000);
  const [wasEmpty, setWasEmpty] = useState(empty);
  if (empty !== wasEmpty) {
    setWasEmpty(empty);
    if (empty) setPick(Math.random() * 1000);
  }

  /**
   * ⚠️ **The hour is read at render, not memoised.** A greeting chosen at midnight and still on
   * screen at one in the morning is a thread nobody came back to; re-reading costs nothing and the
   * alternative is a stale clock.
   *
   * ⚠️⚠️ **THE GIVEN NAME IS ITS OWN FIELD, NOT A SPLIT OF THE COMPOSED ONE.** `0148` carries
   * it on `GET /auth/me` precisely so nothing here has to decide whether *"Takács Gábor"* is
   * family-name-first. An account that never filled the two fields in has none, and then
   * `agent.greeting` is the line — a greeting with no name, rather than *"Ébren, ? Én is."*
   */
  const name = session.givenName;
  const hello =
    name === null
      ? { key: 'agent.greeting' as const, name: null }
      : { key: greetingKey(new Date().getHours(), pick), name };

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE MOCKUP PUTS THESE TWO CONTROLS IN THE PAGE HEADER, NOT ON     │
   * │ THE SCREEN (`markup.html` 261–270).                                   │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * *Korábbi beszélgetések* is a dropdown and *Új beszélgetés* is an outline button, both in the
   * shell's header bar beside the title — which is why they arrive through `usePageHeader` rather
   * than being drawn here. C17 declined both with *"there are no conversations, and there is no table
   * to keep one in"*; 0042 built the table and this is the menu.
   *
   * ⚠️ **The dependency list is VALUES, never the callbacks** — `page-header.tsx` documents the render
   * loop that pegged the main thread when somebody declared a function there. What the header displays
   * is the number of threads and whether one is open.
   */
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C53 · THE SETUP BUTTON HANGS OFF A DIFFERENT FLAG FROM THIS       │
   * │ SCREEN, AND READING IT HERE IS WHAT MAKES IT APPEAR AT ALL.           │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * The Agent is `agent.chat`; the MCP server is `mcp.access`, and the two are switched on
   * separately. So a workspace can perfectly well have this screen and no MCP endpoint, and a
   * button that hands somebody a setup instruction leading to a blanket refusal is worse than no
   * button.
   *
   * ⚠️ **`=== true`, which is the REVERSE of `app.tsx`'s `featureAllows`, deliberately.** That one
   * treats *not answered yet* as *yes*, because hiding a shipped feature's navigation for a moment
   * is the worse failure. Here the flag defaults to **off** and arrives with the sidebar read a
   * beat after mount — so *not answered* must mean *not yet*, and the button appears when the
   * answer does.
   *
   * ⚠️⚠️ **AND IT HAS TO BE IN `deps` OR IT NEVER APPEARS.** `usePageHeader` rebuilds the node only
   * when a declared value changes; the flag flipping from `undefined` to `true` after the sidebar
   * resolves is exactly such a change, and leaving it out means the header is built once, without
   * the button, and never again.
   */
  const mcpOn = useSidebarContext()?.view.features[MCP_FLAG] === true;

  usePageHeader(
    () => (
      <ListHeader
        title={t('title.agent')}
        action={
          <History
            conversations={conversations}
            loading={conversationsLoading === true}
            openId={openId}
            mcpOn={mcpOn}
            onOpen={onOpen}
            onNew={onNew}
            onDelete={onDelete}
            onRename={onRename}
          />
        }
      />
    ),
    /* ⚠️ 29 Oct 2026 · the TITLES too, not only how many there are: a provisional title, its
       summary and a rename all change a name without changing the count, and a header built on the
       count alone would keep naming the thread by its first question for ever. */
    [
      conversations.length,
      conversations.map((entry) => `${entry.id}\u0000${entry.title ?? ''}`).join('\u0001'),
      conversationsLoading,
      openId,
      mcpOn,
      t('title.agent'),
    ],
  );

  /**
   * ⚠️ **The composer holds text even when it is blocked, and C17 argued this**: somebody will type
   * into a text box — that is what a text box is for — and a field that silently refuses keystrokes
   * reads as a broken page rather than as a disabled feature. The notice below it is what explains
   * where the text is going, which when blocked is nowhere.
   */
  const canSend = draft.trim() !== '' && !busy && blocked === null;

  const submit = (): void => {
    if (!canSend) return;
    onSend(draft.trim());
    setDraft('');
  };

  const keyDown = (event: KeyboardEvent<HTMLTextAreaElement>): void => {
    /* The mockup's own binding (`main.jsx` 3669): Enter sends, Shift+Enter is a newline. */
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C47 · ONE PARTITION, AND IT IS THE WHOLE DIFFERENCE FROM C43.       │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * C43's strip listed the conversation's every file, for a stated reason: *"a conversation's
   * attachments are readable by every turn in it — the model is told about all of them on each
   * question — so putting a chip under the message it arrived with would say something narrower than
   * the truth."* Both halves of that are still true, and the conclusion was still wrong, because the
   * strip was answering a question nobody was asking there. Above a composer, *"what will go with
   * what I am about to send"* is the question; after four turns C43's strip answered it with four
   * chips, none of them new, and there was nowhere at all to see which question a file had arrived
   * with.
   *
   * So: pending here, sent above its own message, **all of them in the panel** — which is where
   * *"what can the Agent see"* now gets its complete answer. Three surfaces, one list, one
   * predicate.
   */
  const pending = files.filter((file) => file.messageId === null);

  /**
   * ┌───────────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ C48-1 · THE DROP TARGET IS THE WHOLE SCREEN, NOT THE COMPOSER.      │
   * └───────────────────────────────────────────────────────────────────────────┘
   *
   * `import-dialog.tsx` draws a 200px dashed box and drops onto it, which is right there: the box is
   * the only thing in the dialog and somebody opened the dialog in order to aim at it. Here the
   * composer is a 44px strip at the bottom of a scrolling transcript, and a person dragging a file
   * from Finder is looking at the file rather than at a target — so aiming at the strip is a miss,
   * and a miss means the BROWSER takes the drop and navigates away from the app. Making the whole
   * screen accept it turns the commonest gesture into the working one.
   *
   * ⚠️ **It cannot cover the sidebar or the header, and that is the honest limit of this.** The
   * handlers are on this screen's own root; a drop 20px outside it is still the document's, and the
   * document still wins. `debt.md` carries the thought that only an app-shell-level guard closes
   * that, which is a decision about every screen rather than a line in this one.
   *
   * ⚠️ **Disabled while a batch is uploading**, matching the paperclip beside it — and for a sharper
   * reason than symmetry: `attach` creates the conversation when there is none, so two overlapping
   * calls on a brand-new thread would create two of them.
   */
  const drop = useFileDrop({
    enabled: blocked === null && !attaching,
    onFiles: onAttach,
  });

  /**
   * ⚠️ **One root for both layouts.** The greeting and the open thread are two returns below, and a
   * drop target spelled out twice is one that gets fixed in one of them — the same argument
   * `PROSE_COLUMN` makes about a width.
   */
  const root = {
    'data-screen': marker,
    className: 'relative flex min-h-0 flex-1 flex-col',
    ...drop.handlers,
  };

  const composer = (
    <>
      <Composer
        /* ⚠️ The pending strip is drawn INSIDE the box — `AgentPendingFiles` says why. */
        pending={<AgentPendingFiles files={pending} onRemove={onRemoveFile} busy={busy} />}
        draft={draft}
        onDraftChange={setDraft}
        onKeyDown={keyDown}
        onSubmit={submit}
        onStop={onStop}
        onAttach={onAttach}
        attaching={attaching}
        onBrowse={() => {
          onShowLibrary(true);
        }}
        canSend={canSend}
        busy={busy}
        blocked={blocked}
        placeholder={turns.length === 0 ? t('agent.placeholder') : t('agent.reply.placeholder')}
        noticeId={NOTICE_ID}
      />
      {/**
       * ⚠️ **Rendered from the composer rather than from the screen's root**, so both layouts — the
       * empty greeting and the open thread — get it from one place. `Sheet` portals to the body, so
       * where it sits in this tree costs nothing.
       */}
      <AgentFileLibrary
        open={libraryOpen}
        onOpenChange={onShowLibrary}
        thread={files}
        library={library}
        loading={libraryLoading}
        attaching={attaching}
        onAttach={onAttachExisting}
      />
    </>
  );

  /* ── the mockup's empty state: centred, 620px (`markup.html` 1504–1525) ─────────────────── */
  if (turns.length === 0 && phase === 'idle') {
    return (
      <div {...root}>
        <DropVeil over={drop.over} t={t} />
        {/* 16 Oct 2026 · `isolate` so the glow's `-z-10` stays behind the composer rather than
            behind the page, and `overflow-hidden` so a 1240 px light never adds a scrollbar. */}
        <div className="relative isolate flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-5">
          <div className="w-full max-w-[620px]">
            {/**
             * ┌────────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ THE MARK, ON THE GREETING'S OWN LINE — not the wordmark above it.     │
             * └────────────────────────────────────────────────────────────────┘
             *
             * The first attempt stacked the full wordmark above the sentence, which reads as a
             * title bar on a page that already has one. What was asked for is the shape Claude's
             * own composer uses: the MARK, inline, one line with the words — a flourish at the
             * start of a sentence rather than branding over the top of it.
             *
             * ⚠️ **One file and no theme swap, unlike the wordmark.** `Logo mark.svg` is three
             * coloured paths (`#4E75D9`, `#59C7C7`, `#B541BF`) and legible on both palettes. The
             * WORDMARK needed a light and a dark copy because it is a single flat fill —
             * `auth-frame.tsx` records why a white one on a near-white page is *"not slightly off,
             * but nothing at all"*. That trap does not apply to a mark that carries its own hues.
             *
             * ⚠️ **`em` rather than `px`, so it tracks the heading it sits in.** The mark is taller
             * than it is wide (116 × 193), so the height is what to set and the width follows.
             *
             * ⚠️ **Decorative: `alt=""`.** The sentence beside it greets the reader by name; a
             * screen reader announcing the product first is noise in front of that.
             */}
            <h1 className="flex items-center justify-center gap-2.5 text-center text-[26px] leading-tight font-medium tracking-tight">
              <img src={mark} alt="" className="h-[1.15em] w-auto shrink-0" />
              <span>{t(hello.key, hello.name === null ? {} : { name: hello.name })}</span>
            </h1>
            <div className="relative mt-6">
              <ComposerGlow around="composer" />
              {composer}
            </div>
            <Notice t={t} blocked={blocked} failure={failure} />
          </div>
        </div>
      </div>
    );
  }

  /* ── the thread (`markup.html` 1528–1560) ──────────────────────────────────────────────── */
  return (
    <div {...root}>
      <DropVeil over={drop.over} t={t} />
      <Thread
        turns={turns}
        files={files}
        phase={phase}
        onOpenTask={onOpenTask}
        onExport={onExport}
        onAcceptProposal={onAcceptProposal}
        onDeclineProposal={onDeclineProposal}
        onReviseProposal={onReviseProposal}
      />
      <div className="shrink-0 px-5 pb-6">
        <div className={PROSE_COLUMN}>
          {composer}
          <Notice t={t} blocked={blocked} failure={failure} />
        </div>
      </div>
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C48-1 · WHAT A DRAGGED FILE SEES. Over the whole screen, and inert.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **`pointer-events-none` IS LOAD-BEARING AND NOT A DETAIL.** This element appears *because* a
 * drag entered the screen, so it appears under the pointer mid-gesture — and an element that takes
 * pointer events would fire `dragenter` on itself the moment it mounted, then `dragleave` on the
 * root as the pointer "left" for it, flickering the thing it exists to show. Inert, it is invisible
 * to the drag entirely and the root keeps hearing every event.
 *
 * ⚠️ **Rendered only while `over`**, rather than always-mounted at zero opacity. There is nothing to
 * transition from — the drag either is or is not over the screen — and an always-present overlay is
 * one more thing between a reader and the transcript for every second nobody is dragging anything.
 *
 * ⚠️ **The dashed border and `border-primary bg-primary/[0.06]` are `import-dialog.tsx`'s**, which
 * took them from `markup.html` 2768–2850. The mockup draws no drop target for the Agent at all, so
 * the second authority applies (`docs/design-system/readme.md`) — and inside that, matching the one
 * drop target the product already has beats inventing a second look for the same gesture. ⚠️ **No
 * blur on the scrim**: the design system's own line is *"no backdrop blur, no frosted glass"*.
 */
function DropVeil({ over, t }: { readonly over: boolean; readonly t: Translate }): ReactNode {
  if (!over) return null;

  return (
    <div
      /* ⚠️ `aria-hidden`: a drag is a pointer gesture with no keyboard equivalent, so there is
         nobody to announce this to who could have started it. The paperclip is the accessible path
         to the same action and it is labelled. */
      aria-hidden
      className="bg-background/80 pointer-events-none absolute inset-0 z-10 p-4"
    >
      {/**
       * ┌───────────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ THE DASHED FRAME IS THE WHOLE SCREEN, NOT A BOX IN THE MIDDLE OF IT. │
       * └───────────────────────────────────────────────────────────────────────────┘
       *
       * Item 15, 15 Sep. The drop ZONE was always the whole screen — the handlers are on the
       * screen's root and always have been — but the frame was a 300px card floating in the
       * centre, so the two disagreed: the target somebody could see was a tenth of the target
       * that actually worked, and dropping in the empty space beside it felt like a miss even
       * though it landed.
       *
       * ⚠️ **A veil says where the file will go, so it has to be the shape of where the file will
       * go.** The frame now traces the drop zone's own edges, with the icon and the words centred
       * inside it — which is how Claude's own composer does it, named by the report.
       */}
      <div className="border-primary bg-primary/[0.06] flex h-full w-full flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-10 py-8 text-center">
        <span className="bg-muted text-muted-foreground grid size-12 place-items-center rounded-xl">
          <Icon name="upload" size={22} />
        </span>
        <span className="text-[13px] font-medium">{t('agent.drop.title')}</span>
        {/**
         * ⚠️ **Both halves come from the constants rather than the sentence.** The extensions are
         * the list the server refuses on and the picker offers; the ceiling is `FILE_MAX_BYTES`.
         *
         * ⚠️ **Divided by 1024², not by a million.** `FILE_MAX_BYTES` is `15 * 1024 * 1024`, so the
         * decimal conversion `import-dialog.tsx` uses — correct there, because `IMPORT_MAX_BYTES` is
         * a round 5 000 000 — would print *16 MB* under a 15 MB limit.
         */}
        <span className="text-muted-foreground text-[11.5px]">
          {t('agent.drop.hint', {
            /* ⚠️ Pictures too, since feedback round 4 item 14 — and derived from the constants for
               the reason the note above gives: a hint naming a format the picker refuses is worse
               than no hint. */
            kinds: [...TABLE_EXTENSIONS, ...AGENT_IMAGE_EXTENSIONS].join(', '),
            mb: String(Math.round(FILE_MAX_BYTES / (1024 * 1024))),
          })}
        </span>
      </div>
    </div>
  );
}

/**
 * ⚠️ **One id, and the composer points at it with `aria-describedby`.** The line underneath is the
 * answer to *"why did nothing happen when I pressed Enter"*, and a sighted reader gets it by
 * proximity. C17 made the same call for the same reason.
 */
const NOTICE_ID = 'agent-notice';

/**
 * ┌───────────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE READING AXIS, NAMED ONCE. THE COMPOSER, THE PROSE AND THE WORKING   │
 * │ INDICATOR ARE ALL ON IT.                                                     │
 * └───────────────────────────────────────────────────────────────────────────────┘
 *
 * 680px is the mockup's own thread width (`markup.html` 1528–1560); the thread's 1040px is the
 * flagged departure above, and only a data card uses it.
 *
 * ⚠️ **A constant rather than the number three times, because the drift is the bug.** The working
 * indicator was written outside this column and rendered ~180px to the left of the sentence it was
 * announcing — a whole indicator on its own axis, and nothing in the file said the axis was shared.
 * Now it is one string, and a fourth thing put on the reading axis cannot pick a different number by
 * accident.
 *
 * ⚠️ **`agent-proposal-card.tsx` carries the same 680px in its own root**, deliberately: the card
 * centres itself so a caller cannot render it off-axis. If this number ever changes, that one
 * changes with it — the pairing is stated at both ends.
 */
const PROSE_COLUMN = 'mx-auto w-full max-w-[680px]';

function Notice({
  t,
  blocked,
  failure,
}: {
  readonly t: Translate;
  readonly blocked: 'notConnected' | 'notInPlan' | null;
  readonly failure: string | null;
}): ReactNode {
  /**
   * ⚠️⚠️ **THREE MUTUALLY EXCLUSIVE SENTENCES, IN THIS ORDER, AND THE ORDER IS THE ARGUMENT.**
   *
   *  1. **Blocked** wins: if the agent cannot be used at all, a message about the last turn is noise.
   *  2. **A failure** next: it is about something the person just did.
   *  3. **`agent.readOnly`** otherwise — the standing statement of what this agent is: it reads
   *     rather than remembers, and it cannot change anything yet. It is the quiet one, and it is what
   *     stops somebody asking it to log four hours and reading a polite paragraph as a confirmation.
   */
  const text =
    blocked === 'notConnected'
      ? t('agent.notConnected')
      : blocked === 'notInPlan'
        ? t('agent.notInPlan')
        : (failure ?? t('agent.readOnly'));

  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ A FAILURE IS RED. THE OTHER TWO SENTENCES ARE NOT.               │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * All three used to render in `text-muted-foreground` at 11.5px, in the same spot — so *"a modell
   * nem válaszolt"* was typographically identical to the standing *"az Agent olvas…"* footnote, and
   * it was reported as nothing happening at all. Announced to a screen reader, invisible to an eye.
   *
   * ⚠️ **Only the failure**: `notConnected` and `notInPlan` are states of the deployment, not things
   * that just went wrong, and a permanently red footer under a disabled composer reads as an error
   * somebody should fix rather than a feature nobody turned on.
   */
  const failed = failure !== null && blocked === null;

  return (
    <p
      id={NOTICE_ID}
      /* ⚠️ `role="alert"` for a failure so it is announced, `role="status"` otherwise so the standing
       * notice is polite. A permanent `alert` interrupts a screen reader on every render. */
      role={failed ? 'alert' : 'status'}
      className={`mt-3 text-center text-[11.5px] ${failed ? 'text-destructive' : 'text-muted-foreground'}`}
    >
      {text}
    </p>
  );
}

function Composer({
  draft,
  onDraftChange,
  onKeyDown,
  onSubmit,
  onStop,
  onAttach,
  attaching,
  onBrowse,
  canSend,
  busy,
  blocked,
  placeholder,
  noticeId,
  pending,
}: {
  readonly draft: string;
  readonly onDraftChange: (value: string) => void;
  readonly onKeyDown: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  readonly onSubmit: () => void;
  readonly onStop: () => void;
  /**
   * ⚠️⚠️ **C43 · THE FILE IS UPLOADED WHEN IT IS PICKED, NOT STAGED UNTIL SEND — WHICH IS THE
   * OPPOSITE OF `comment-composer.tsx` AND FOR THE OPPOSITE REASON.** A comment's attachment has
   * nothing to hang on until the comment exists, so it must be staged. A conversation exists before
   * the question does, and the file belongs to the **thread** rather than to one message: so it can
   * go straight away, the chip appears immediately, and somebody can attach two files and then ask
   * about both.
   */
  readonly onAttach: (files: readonly File[]) => void;
  readonly attaching: boolean;
  /** C47 · open the library panel. */
  readonly onBrowse: () => void;
  readonly canSend: boolean;
  readonly busy: boolean;
  readonly blocked: 'notConnected' | 'notInPlan' | null;
  readonly placeholder: string;
  readonly noticeId: string;
  /** ⚠️ What is already attached, drawn above the question and inside this box. */
  readonly pending: ReactNode;
}): ReactNode {
  const t = useT();
  const picker = useRef<HTMLInputElement>(null);

  return (
    /* ⚠️ The focus ring is on the WRAPPER, so the whole box lights up rather than the textarea
       inside it. The mockup's own construction (`markup.html` 1508). */
    <div
      data-tour="agent-composer"
      className="bg-card focus-within:border-ring focus-within:ring-ring/40 rounded-2xl border p-3 shadow-sm transition-[color,box-shadow] focus-within:ring-[3px]"
    >
      {pending}
      <Textarea
        value={draft}
        onChange={(event) => {
          onDraftChange(event.target.value);
        }}
        onKeyDown={onKeyDown}
        /**
         * ┌────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ CTRL+V ATTACHES A PICTURE — feedback round 4 item 14.                 │
         * └────────────────────────────────────────────────────────────────┘
         *
         * ⚠️⚠️ **A SCREENSHOT HAS NO FILE, AND THAT IS THE WHOLE REASON THIS EXISTS.** Print
         * Screen puts an image on the clipboard and nowhere else — so without this, the commonest
         * thing anybody wants to show the agent has to be saved to disk first and then found in a
         * file picker, which is two detours around a gesture every chat window in the world
         * supports.
         *
         * ⚠️ **Only when the paste actually carries a file.** Pasting TEXT must stay a paste: the
         * handler returns without touching the event unless there is an image in the payload, and
         * the browser then inserts the text as it always did.
         *
         * ⚠️ **`preventDefault` only in the image case**, because Chrome also puts a filename on
         * the clipboard for a copied file — letting the default run would paste that name into the
         * question beside the attachment somebody just made.
         */
        onPaste={(event) => {
          if (blocked !== null || attaching) return;
          const pasted = [...event.clipboardData.files].filter((file) =>
            file.type.startsWith('image/'),
          );
          if (pasted.length === 0) return;
          event.preventDefault();
          onAttach(pasted);
        }}
        placeholder={placeholder}
        /* ⚠️ **The contract's own cap, enforced here as well as on the server.** `maxLength` makes it
           a limit somebody can feel rather than a refusal they read after sending — and the server
           still refuses, because UI gating is UX (rule 6). */
        maxLength={AGENT_MESSAGE_MAX_CHARS}
        className="min-h-10 border-0 bg-transparent px-1 shadow-none focus-visible:ring-0"
        aria-describedby={noticeId}
      />
      <div className="mt-1 flex items-center gap-1">
        {/**
         * ⚠️ **The accept list is the server's, and it is now literally the server's list.** C48-1
         * moved the four extensions into `@forecastify/shared` because the drop path needs the same
         * answer and `accept` does nothing at all for a drag-and-drop. It is still a convenience and
         * not the check: `file.service.ts` refuses a non-tabular attachment on this target whatever
         * the dialog offered, because a picker's filter is a suggestion the operating system is free
         * to ignore.
         *
         * ⚠️ **`multiple`, since C48-1.** A drop can carry three files and does; a picker that
         * could not would be the same gesture answering differently depending on which control
         * somebody reached for.
         */}
        <input
          ref={picker}
          type="file"
          multiple
          accept={[...TABLE_EXTENSIONS, ...AGENT_IMAGE_EXTENSIONS].join(',')}
          className="hidden"
          onChange={(event) => {
            const chosen = [...(event.target.files ?? [])];
            /* Cleared, so picking the same file twice still fires `change`. */
            event.target.value = '';
            if (chosen.length > 0) onAttach(chosen);
          }}
        />
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={blocked !== null || attaching}
          aria-disabled={blocked !== null || attaching}
          aria-label={t('agent.attach')}
          title={t('agent.attachHint')}
          data-tour="agent-attach"
          onClick={() => {
            picker.current?.click();
          }}
        >
          {/* ⚠️ `refresh-cw` while an upload is in flight, because a scan of a 15 MB file is not
              instant and a control that looks identical while nothing happens reads as broken.
              There is no spinner glyph in the set — `packages/ui/src/components/icon.tsx` is the
              closed list — and adding one for this is a design-system decision, not a line here. */}
          <Icon name={attaching ? 'refresh-cw' : 'paperclip'} size={14} />
        </Button>
        {/**
         * ┌──────────────────────────────────────────────────────────────────┐
         * │ ⚠️⚠️ C47 · BESIDE THE PAPERCLIP, BECAUSE IT IS THE SAME ACTION   │
         * │ REACHED A DIFFERENT WAY.                                         │
         * └──────────────────────────────────────────────────────────────────┘
         *
         * *Attach a file* and *attach a file I have already sent* belong together: somebody who has
         * uploaded last month's timesheet once should find the second control where they found the
         * first, not in a page header three metres away from the question they are typing.
         *
         * ⚠️ **`folder` rather than a second `paperclip`.** Two identical glyphs side by side is a
         * button nobody can tell apart, and `packages/ui/src/components/icon.tsx` is a closed list —
         * this is the one in it that means *"things you already have"*.
         *
         * ⚠️ **Not disabled while an upload is in flight.** Browsing is a read; only the re-attach
         * inside the panel waits, and its own button says so.
         */}
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={blocked !== null}
          aria-disabled={blocked !== null}
          aria-label={t('agent.files.browse')}
          title={t('agent.files.browse')}
          onClick={onBrowse}
        >
          <Icon name="folder" size={14} />
        </Button>
        <div className="flex-1" />
        {busy ? (
          /**
           * ┌──────────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ STOP IS A REAL REQUEST, NOT A UI RESET.                     │
           * └──────────────────────────────────────────────────────────────────┘
           *
           * Abandoning the read would leave the model generating server-side: tokens produced,
           * billed, and written to a socket nobody is reading. `agent-route.tsx` aborts the fetch,
           * which closes the response, which makes the API's own loop break and cancel the upstream
           * call. Three layers have to agree and they are commented in all three.
           */
          <Button size="icon-sm" variant="secondary" onClick={onStop} aria-label={t('agent.stop')}>
            <Icon name="square" size={13} />
          </Button>
        ) : (
          <Button
            size="icon-sm"
            disabled={!canSend}
            aria-disabled={!canSend}
            aria-label={t('agent.send')}
            /* ⚠️ The tooltip explains a disabled button rather than leaving somebody to guess.
               When blocked, that reason is the notice's; otherwise the box is simply empty. */
            title={blocked !== null ? t('agent.notConnected') : undefined}
            onClick={onSubmit}
          >
            <Icon name="arrow-right" size={15} />
          </Button>
        )}
      </div>
    </div>
  );
}

function Thread({
  turns,
  files,
  phase,
  onOpenTask,
  onExport,
  onAcceptProposal,
  onDeclineProposal,
  onReviseProposal,
}: {
  readonly turns: readonly ThreadTurn[];
  /** C47 · the thread's files; each turn is handed the ones that name it. */
  readonly files: readonly AgentConversationFile[];
  readonly phase: AgentPhase;
  readonly onOpenTask: (key: string) => void;
  /**
   * ⚠️ **C48 · the person runs the export, not the screen and not the model.** The route hands this
   * to `useExport().open()`, which opens the product's own dialog pre-filled — so what this screen
   * does with an offer is exactly what it does with a task key: hand it to whoever owns that surface.
   */
  readonly onExport: (offer: AgentExportOffer) => void;
  readonly onAcceptProposal: (id: string) => void;
  readonly onDeclineProposal: (id: string) => void;
  /** ⚠️ C46 · a correction re-proposes; `agent-proposal-card.tsx` argues why it is not an accept. */
  readonly onReviseProposal: (id: string, values: Readonly<Record<string, string | null>>) => void;
}): ReactNode {
  const t = useT();
  /**
   * ⚠️⚠️ **FOLLOWS THE NEWEST LINE ONLY WHILE THE READER IS AT THE BOTTOM** — `follow-thread.ts`
   * argues it, and closes `debt.md`'s *"the thread scroll fights somebody who has scrolled up"*.
   *
   * ⚠️ **Instant, never `behavior: 'smooth'`.** A smooth scroll animates for ~300ms; deltas arrive
   * every few tens of milliseconds, so each one interrupts the last and the thread crawls behind the
   * text it is following. And `prefers-reduced-motion` needs no branch precisely because this is not
   * an animation.
   *
   * ⚠️ **Held again by the newest QUESTION's id**: somebody who scrolled up and then asks something
   * wants to watch the answer arrive, and opening another thread starts at its end.
   */
  const follow = useFollowThread(turns.filter((turn) => turn.role === 'user').at(-1)?.id ?? null);

  return (
    /**
     * ┌───────────────────────────────────────────────────────────────────────────┐
     * │ ⚠️⚠️ `scrollbar-gutter: stable both-edges` IS AN ALIGNMENT FIX, NOT A     │
     * │ SCROLLBAR PREFERENCE.                                                     │
     * └───────────────────────────────────────────────────────────────────────────┘
     *
     * The composer sits in a sibling of this box and centres at `max-w-[680px]`; so does the prose
     * inside it. They still did not line up, and the missing 5px is the scrollbar: `base.css` styles
     * `::-webkit-scrollbar`, and styling it *at all* opts the whole product out of overlay
     * scrollbars — so this scroller's 10px bar comes out of its CONTENT box, every centred thing in
     * it lands 5px to the left, and the composer, which does not scroll, does not move. Visible as a
     * jog between the person's own right-aligned bubble and the composer's right border, and only
     * once a thread is long enough to overflow.
     *
     * ⚠️ **`both-edges` rather than compensating in the composer.** A padding correction on the
     * sibling would have to know this box's scrollbar width, when it appears, and that Firefox and
     * WebKit measure it differently — three facts CSS already knows. Reserving the same gutter on
     * the left instead keeps one axis for both boxes with no number to keep in step.
     *
     * ⚠️ **`stable`, so it is reserved whether or not the bar is showing** — otherwise the thread
     * would shift sideways at the moment it grows past one screen, which is worse than being 5px
     * off. It costs 10px of the 1040px card's room and nothing at all where scrollbars are overlay,
     * because the property is inert there by design.
     */
    <div
      ref={follow.ref}
      onScroll={follow.onScroll}
      className="min-h-0 flex-1 overflow-y-auto px-5 py-8 [scrollbar-gutter:stable_both-edges]"
    >
      {/**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ 1040px HERE AND 680px FOR THE PROSE — A DEPARTURE FROM A DRAWN   │
       * │ SCREEN, FLAGGED RATHER THAN ABSORBED.                                 │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * `markup.html` 1528–1560 draws the whole thread at `max-w-[680px]`, and its data block is a
       * two-column key/value strip. That is honest for *"hours per project"* and hopeless for a task
       * list: eight columns do not fit 680px, and reducing a task to a name and one number is the
       * bulleted list with extra borders. §4 proposes **1040px** — the mockup's own `max-w` for wide
       * cards elsewhere, so the number already exists in the product — and five columns.
       *
       * ⚠️ **The plan said *"aligned to the same left edge"* and this centres instead**, because a
       * centred 680px column and a left-aligned 1040px card cannot both hold: the card's right edge
       * would leave the content area on any viewport narrower than about 1400px. Centring on the same
       * axis keeps the composer where the mockup puts it and lets the card breathe symmetrically.
       *
       * ⚠️ **`decisions.md` carries this as an open question for the product owner.** It is a
       * departure from a drawn screen, and those are the ones this project flags.
       */}
      <div className="mx-auto flex w-full max-w-[1040px] flex-col gap-7">
        {turns.map((turn, index) => (
          <Turn
            key={turn.id}
            turn={turn}
            /* ⚠️ **Only the last assistant turn, and only while something is in flight.** The draft
             * the route appends on `message_start` is always last; the person's own turn is last
             * during `sending`, and nothing in it reorders. */
            live={phase !== 'idle' && index === turns.length - 1 && turn.role === 'assistant'}
            /* ⚠️ **Filtered here rather than grouped once above**, because a thread holds a handful of
             * files and a `Map` built per render to serve five rows is machinery standing in for a
             * comparison. If a thread ever holds hundreds, the grouping goes in `agent-route.tsx`
             * beside the fetch — not here, where it would be recomputed on every streamed delta. */
            files={files.filter((file) => file.messageId === turn.id)}
            onOpenTask={onOpenTask}
            onExport={onExport}
            onAcceptProposal={onAcceptProposal}
            onDeclineProposal={onDeclineProposal}
            onReviseProposal={onReviseProposal}
          />
        ))}

        {/* ⚠️ Only while nothing has arrived yet. Once text is streaming, the text IS the indicator,
            and a spinner beside a filling bubble reads as a second thing happening. */}
        {phase === 'sending' ? <Working label={t('agent.working')} /> : null}
        {phase === 'thinking' && (turns.at(-1)?.thinking ?? null) === null ? (
          <Working label={t('agent.thinking')} />
        ) : null}
      </div>
    </div>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C48 · an export the model set up, for a person to run. R-34              │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **A CONTROL, NOT A CARD, AND NOT A FILE.** Nothing has been exported when this renders: the
 * model resolved a filter — *"August's hours on Acme"* into a domain, two dates and a project id —
 * and pressing this opens the product's **own** export dialog with those values filled in. The
 * person presses Export there. `export.tools.ts` argues why it is that way round rather than R-25's
 * confirmation card, and the short version is that past `EXPORT_INLINE_LIMIT` an export is an
 * **email**: a model must not be able to send mail by inferring a date range.
 *
 * ⚠️ **One line and one button, deliberately not a bordered card.** A card would compete with the
 * confirmation card, which is the surface that means *"a decision is being asked of you"*. This
 * means *"here is a shortcut"*, and the design system's hairline-and-muted register is where that
 * belongs.
 *
 * ⚠️⚠️ **IT STATES WHAT IT WILL EXPORT, IN THE READER'S OWN LANGUAGE, FROM MACHINE PARTS.** The
 * domain is an enum through `domainLabel`; the dates and the resolved names are data (`projectName`
 * is a name, exactly as `statusName` is on a task). No frame carries prose, so the sentence is
 * composed here — and it has to be composed at all, because an offer nobody can read is one nobody
 * can check before pressing.
 */
function AgentExportOfferControl({
  offer,
  onExport,
}: {
  readonly offer: AgentExportOffer;
  readonly onExport: (offer: AgentExportOffer) => void;
}): ReactNode {
  const t = useT();

  /**
   * ⚠️ **The parts, joined with a middot — the same rendering `agent-proposal-card.tsx` uses for a
   * row's fields.** A filter with nothing in it is *"every row"*, said rather than left blank: an
   * unqualified *Exportálás* button is one nobody knows the scope of.
   */
  const parts = [domainLabel(t, offer.domain)];

  if (offer.projectName !== null) parts.push(offer.projectName);
  if (offer.contractCode !== null) parts.push(offer.contractCode);
  if (offer.memberName !== null) parts.push(offer.memberName);

  /* ⚠️ **An open end is said, not omitted.** `2026-08-01 – ` reads as a broken string; *"…-ig"* /
   * *"until"* is what a one-sided range actually means. */
  if (offer.from !== null && offer.to !== null) parts.push(`${offer.from} – ${offer.to}`);
  else if (offer.from !== null) parts.push(t('agent.export.since', { day: offer.from }));
  else if (offer.to !== null) parts.push(t('agent.export.until', { day: offer.to }));
  else parts.push(t('agent.export.everything'));

  const what = parts.join(' · ');

  return (
    <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
      <Icon name="download" size={13} className="text-muted-foreground shrink-0" />
      <TruncatedText className="text-muted-foreground flex-1">{what}</TruncatedText>
      {/* ⚠️ **`outline`, not the primary fill.** The primary button in this thread is *Küldés*, and
          the one on a confirmation card is a write; an offer is neither the thing you came for nor a
          decision, so it does not get the loudest button on screen. */}
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          onExport(offer);
        }}
      >
        {t('agent.export.run')}
      </Button>
    </div>
  );
}

/**
 * The typing indicator, label parameterised.
 *
 * ⚠️ **On `PROSE_COLUMN`, because it stands in for the sentence that is about to arrive.** It was a
 * direct child of the thread's 1040px column, so *Gondolkodik…* began where a wide data card begins
 * and every reply began 180px further in — the indicator slid out past the conversation it belonged
 * to. It announces prose, so it lives on the prose axis.
 *
 * ⚠️⚠️ **THE MOCKUP'S PULSING DOT IS GONE** — *"a really boring animation"*, and it was: one
 * `size-1.5` circle with `animate-pulse`. `thinking-orb.tsx` is the replacement the product owner
 * named, and argues the size and the colour, neither of which the package offers as a prop.
 */
function Working({ label }: { readonly label: string }): ReactNode {
  return (
    <div
      role="status"
      className={`${PROSE_COLUMN} text-muted-foreground flex items-center gap-2 text-[12.5px]`}
      data-agent-working
    >
      <ThinkingIndicator label={label} />
      {label}
    </div>
  );
}

function Turn({
  turn,
  files,
  live,
  onOpenTask,
  onExport,
  onAcceptProposal,
  onDeclineProposal,
  onReviseProposal,
}: {
  readonly turn: ThreadTurn;
  /** C47 · the files sent with this message. Empty for every turn that carried none, which is most. */
  readonly files: readonly AgentConversationFile[];
  /**
   * ┌───────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THIS TURN IS STILL ARRIVING, SO ITS REASONING SITS UNDER THE    │
   * │ TOOL ROWS RATHER THAN ABOVE THEM.                                     │
   * └───────────────────────────────────────────────────────────────────────┘
   *
   * Reported 28 Oct 2026: a turn that reads twelve projects grows twelve rows under the reasoning
   * disclosure, and somebody who opened it to follow along watches the model's words recede
   * upwards while the thing moving on screen is a list of ticks. While the turn is live the
   * reasoning is the newest thing — every `thinking_delta` appends to it — so it is pinned to the
   * bottom of the bubble, beside the text that is about to arrive. Once the turn settles the order
   * returns to the one it happened in: thought, read, answered.
   */
  readonly live: boolean;
  readonly onOpenTask: (key: string) => void;
  /**
   * ⚠️ **C48 · the person runs the export, not the screen and not the model.** The route hands this
   * to `useExport().open()`, which opens the product's own dialog pre-filled — so what this screen
   * does with an offer is exactly what it does with a task key: hand it to whoever owns that surface.
   */
  readonly onExport: (offer: AgentExportOffer) => void;
  readonly onAcceptProposal: (id: string) => void;
  readonly onDeclineProposal: (id: string) => void;
  /** ⚠️ C46 · a correction re-proposes; `agent-proposal-card.tsx` argues why it is not an accept. */
  readonly onReviseProposal: (id: string, values: Readonly<Record<string, string | null>>) => void;
}): ReactNode {
  const t = useT();
  const mine = turn.role === 'user';

  return (
    <div className={`flex flex-col gap-1.5 ${mine ? 'items-end' : 'items-start'}`}>
      {/* ⚠️ **The prose column stays at the mockup's 680px, centred**, whatever the thread's own
          width is. Only a data card takes the extra room — see the note on the thread. */}
      <div className={`${PROSE_COLUMN} flex flex-col gap-1.5`}>
        <div className={`group/turn flex flex-col gap-1.5 ${mine ? 'items-end' : 'items-start'}`}>
          {/**
           * ┌──────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ C48 · THE SPEAKER'S NAME, AND — ON A REPLY — A WAY TO   │
           * │ KEEP IT.                                                     │
           * └──────────────────────────────────────────────────────────────┘
           *
           * ⚠️ **`min-h-[20px]` on both speakers, so the row does not change height when a button
           * is in it.** The control is `size="sm"` (`h-7`, the product's smallest button — the
           * design system's numbers are the design) pulled in with `-my-1`; without a floor on the
           * row, an agent turn's header would stand 6px taller than a person's and the thread's
           * rhythm would depend on who was talking.
           *
           * ⚠️ **Hover-revealed, which `design-system/readme.md` sanctions** — *"row checkboxes,
           * row menus, inline edit pencils … sit at `opacity-0` until hover or focus-within, so
           * dense lists stay quiet at rest"*. A download icon on every reply in a long thread is
           * exactly that kind of noise.
           *
           * ⚠️⚠️ **`focus-visible:opacity-100` IS THE ACCESSIBILITY HALF AND IS NOT OPTIONAL.**
           * `opacity-0` still focuses and still reads; rendering the button only while hovered
           * would leave nothing for a keyboard to reach, which is how a hover-revealed control
           * becomes a control some people do not have.
           */}
          <div className="flex min-h-[20px] items-center gap-1.5">
            <span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">
              {mine ? t('agent.you') : t('agent.agent')}
            </span>
            {/**
             * ⚠️⚠️ **THE REPLY ONLY, AND THE FILE IS THE REPLY — NOT AN EXPORT OF ANYTHING.** What
             * it saves is `turn.text`, byte for byte, with no header, no date line and no
             * workspace name. A file carrying the product's own framing around the model's words
             * would read as something the product produced from its data, which is the confusion
             * R-33 spent a table's worth of argument avoiding. The FILENAME says where it came
             * from; the contents are a copy of a chat message.
             *
             * ⚠️ **Not on the person's own turn**, which they wrote and already have, and not on an
             * empty one — a failed turn has no text and would offer a file with nothing in it.
             *
             * ⚠️ **`.md` is now a true statement about the bytes.** It was not before C48: the
             * model was told to write plain text, so a file called `.md` would have been a plain
             * paragraph wearing a format's extension — the same lie as a CSV renamed `.xlsx`
             * (`chunks.md` refuses that one by name).
             */}
            {mine || turn.text === '' ? null : (
              <Button
                variant="ghost"
                size="sm"
                className="-my-1 opacity-0 transition-opacity group-hover/turn:opacity-100 focus-visible:opacity-100"
                onClick={() => {
                  saveTextFile(
                    exportFilename('agent', new Date().toISOString().slice(0, 10), 'md'),
                    turn.text,
                    'text/markdown;charset=utf-8',
                  );
                }}
                aria-label={t('agent.turn.download')}
              >
                <Icon name="download" size={13} />
              </Button>
            )}
          </div>
          {/**
           * ┌───────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ C47 · ABOVE THE MESSAGE, UNDER THE *YOU* LABEL.          │
           * └───────────────────────────────────────────────────────────────┘
           *
           * ⚠️ **Above rather than below**, which is the order it happened in: the file was picked,
           * then the question was typed. It also keeps the bubble against whatever follows it — a
           * reply, a card, a confirmation — instead of putting a strip of filenames between a
           * question and its answer.
           *
           * ⚠️ **Only ever on the person's own turn**, and not by a branch here: the Agent produces
           * no files, so `messageId` never names an assistant row and the list is empty. Worth
           * stating, because a reader looking for the `mine` check will not find one.
           */}
          <AgentFileChips files={files} />
          {/* The mockup's own two bubble shapes (`main.jsx` 3664): mine is a filled pill, the agent's
              is bare full-width prose. */}
          <div
            className={
              mine ? 'bg-muted max-w-[80%] rounded-2xl px-3.5 py-2.5' : 'min-w-0 max-w-full'
            }
          >
            {/* ⚠️ **The tool rows sit ABOVE the prose and, once settled, below the reasoning**, which
                is the order they happened in: the model thought, it read, then it answered. A
                transcript whose evidence appears after the conclusion is one nobody reads.
                ⚠️ **While the turn is LIVE the two swap** — `live` on the props argues it — and
                they swap as KEYED siblings, so React moves the `<details>` node rather than
                remounting it: a disclosure somebody opened mid-turn stays open when the turn
                settles and the row slides back above the ticks. */}
            {/* 29 Oct 2026 · first in the bubble, because the summary was written before any of
                it: the API summarises and then answers from the summary. */}
            {!mine && turn.compacted ? (
              <p
                className="text-muted-foreground mb-2 flex items-center gap-1.5 text-[11.5px]"
                data-agent-compacted
              >
                <Icon name="file-text" size={12} aria-hidden />
                {t('agent.turn.compacted')}
              </p>
            ) : null}
            {(() => {
              const reasoning =
                turn.thinking === null ? null : <Reasoning key="reasoning" text={turn.thinking} />;
              const steps =
                turn.steps.length === 0 ? null : <Steps key="steps" steps={turn.steps} />;
              return live ? [steps, reasoning] : [reasoning, steps];
            })()}
            {/**
             * ┌──────────────────────────────────────────────────────────────┐
             * │ ⚠️⚠️ C48 · THE MODEL'S TEXT IS MARKDOWN. THE PERSON'S IS NOT. │
             * └──────────────────────────────────────────────────────────────┘
             *
             * Reported: a reply with `**bold**`, a list or a table in it arrived as characters.
             * `agent-markdown.tsx` renders it — and carries the two arguments worth knowing
             * before touching this: the preamble's `VOICE` paragraph is the coupled half, and a
             * table of the model's own rows is deliberately NOT drawn with `DataList` (R-33).
             *
             * ⚠️ **The person's own message keeps the plain path**, and that is the whole reason
             * this is a branch rather than one component: somebody who typed `2 * 3 * 4` wrote
             * arithmetic, and parsing their message as markup would eat characters they typed.
             * `whitespace-pre-line` is what makes their newlines survive, and `AgentProse` splits
             * the string without trimming it so they still do.
             *
             * ⚠️⚠️ **C45 · THE KEYS ARE LINKS ON BOTH SIDES.** A card rendered rows a service
             * returned; both of these render text somebody wrote, and turn three shapes of
             * identifier into an address without displaying a single figure of their own.
             * `agent-prose.tsx` argues it, including why nothing that merely looks like a URL is
             * linked. A key is a key: somebody who pasted `HSO-131` wants to open it as much as
             * the reader of the answer does.
             */}
            {turn.text === '' ? null : mine ? (
              <p className="text-[13.5px] leading-relaxed whitespace-pre-line">
                <AgentProse text={turn.text} />
              </p>
            ) : (
              <div className="text-[13.5px] leading-relaxed">
                <AgentMarkdown text={turn.text} />
              </div>
            )}
          </div>
          {/**
           * ┌───────────────────────────────────────────────────────────────┐
           * │ ⚠️⚠️ OUTSIDE THE BUBBLE AND UNDER IT, IN THE DESTRUCTIVE      │
           * │ COLOUR.                                                       │
           * └───────────────────────────────────────────────────────────────┘
           *
           * ⚠️ **Outside**, because the bubble is what the person said and this is not: putting it
           * inside would make a failure look like part of their own sentence.
           *
           * ⚠️ **`text-destructive`, which is the token and not a hex red** — `design-system`'s
           * palette owns what destructive looks like in both themes, and a literal here would be
           * the one red on this screen that stops following the theme.
           *
           * ⚠️ **`role="status"`, not `alert`.** On reload this renders for every failed turn in
           * the history at once, and three alerts on mount interrupt a screen reader three times
           * over something that already happened. The LIVE announcement is the notice under the
           * composer, which is an `alert` for exactly this reason.
           */}
          {turn.failed ? (
            <p role="status" className="text-destructive text-[11.5px]">
              {t('agent.turn.noAnswer')}
            </p>
          ) : null}
        </div>
      </div>

      {/* ⚠️ **Outside the 680px column, so a table can use the width.** Below the prose, because the
          sentence is the answer and the table is its evidence. */}
      {turn.blocks.map((block, index) => (
        <div
          /* ⚠️ Index-keyed, and it is safe here for a reason worth stating: blocks are appended to a
             turn and never removed or reordered — a card's position in a finished turn is a fact. */
          key={`${turn.id}:${String(index)}`}
          className="w-full"
        >
          <AgentDataCard block={block} onOpenTask={onOpenTask} />
        </div>
      ))}

      {/**
       * ┌───────────────────────────────────────────────────────────────────────┐
       * │ ⚠️⚠️ C48 · THE EXPORTS THIS TURN OFFERED, ON THE READING AXIS. R-34.  │
       * └───────────────────────────────────────────────────────────────────────┘
       *
       * ⚠️ **`PROSE_COLUMN`, not the table width.** It belongs to the sentence above it — *"itt van
       * az export, nyomd meg"* — and a control 360px wider than the words that introduce it puts
       * the button somewhere the eye is not. Same argument as the confirmation card below.
       *
       * ⚠️ **Above the confirmation card**, because a turn can hold both and the card is the thing
       * that changes the workspace: whatever asks for a decision goes last.
       */}
      {turn.offers.map((offer, index) => (
        <div key={`${turn.id}:offer:${String(index)}`} className={PROSE_COLUMN}>
          <AgentExportOfferControl offer={offer} onExport={onExport} />
        </div>
      ))}

      {/* ⚠️ **Last in the turn, and at the PROSE width rather than the table width.** The order is
          the order it happened in: the model said what it prepared, showed whatever it read, and then
          asked. And a card with two buttons on it belongs on the reading axis — 680px is where the
          sentence above it sits, and a 1040px confirmation would put *Rögzítés* somewhere the eye is
          not. */}
      {turn.proposal === null ? null : (
        <div className="w-full">
          <AgentProposalCard
            proposal={turn.proposal}
            onAccept={onAcceptProposal}
            onDecline={onDeclineProposal}
            onRevise={onReviseProposal}
          />
        </div>
      )}
    </div>
  );
}

/**
 * The tool rows.
 *
 * ⚠️⚠️ **A ROW CARRIES THE TOOL'S NAME THROUGH THE CATALOGUE, NEVER A SENTENCE FROM THE SERVER.**
 * `tool_start` carries `tasks_search`; rule 7 and the frame contract's header both say why — the
 * server does not know the reader's locale, and a locale on the wire would mean the API owning half
 * the catalogue.
 */
function Steps({ steps }: { readonly steps: readonly ThreadStep[] }): ReactNode {
  const t = useT();

  return (
    <ul className="mb-2 flex flex-col gap-1" data-agent-steps>
      {stepRows(t, steps).map((row) => (
        <li
          key={row.id}
          className="text-muted-foreground flex items-center gap-1.5 text-[11.5px]"
          data-agent-step={row.state}
          data-agent-step-count={row.count}
        >
          {row.state === 'running' ? (
            <span className="bg-muted-foreground/60 size-1.5 animate-pulse rounded-full" />
          ) : (
            <Icon
              name={row.state === 'failed' ? 'triangle-alert' : 'check'}
              size={12}
              className={row.state === 'failed' ? 'text-destructive' : undefined}
              aria-hidden
            />
          )}
          {row.label}
          {/* ⚠️ The kit's `Badge` — *"11px status/count chip, tabular figures"* — rather than a
              bracketed number in the sentence: the count is a count of ROWS, not a figure the tool
              returned, and a chip keeps it visibly apart from *"12 feladat"*. */}
          {row.count > 1 ? (
            <Badge variant="secondary">{t('agent.tool.repeated', { count: row.count })}</Badge>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** One line of the tool list: a label, and how many identical calls it stands for. */
interface StepRow {
  /** The first folded step's id — stable while later identical steps join the row. */
  readonly id: string;
  readonly state: ThreadStep['state'];
  readonly label: string;
  readonly count: number;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ IDENTICAL ROWS FOLD INTO ONE LINE WITH A COUNT.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Reported 28 Oct 2026: ask about twelve projects and the model reads each one, which drew
 * *"Projekt beolvasva"* twelve times under the reply. Twelve identical lines carry one fact — *it
 * read the projects* — and the number of them is the only part worth a glance.
 *
 * ⚠️ **Folded on the RENDERED label and the state, not on the tool name.** Two task searches that
 * found 12 and 3 rows are different evidence and stay two lines; two that both say *"12 feladat"*
 * are one line with `2×` on it. The label is what the reader compares, so it is what the fold
 * compares. A running call has its own label and so is always its own row — it joins the done row
 * when it settles, which is the counter visibly ticking up.
 *
 * ⚠️ **Across the whole turn, not only adjacent rows**, in first-occurrence order. The model that
 * alternates *project, tasks, project, tasks* six times over is the case this is for, and adjacency
 * would fold nothing there.
 */
function stepRows(t: Translate, steps: readonly ThreadStep[]): readonly StepRow[] {
  /* A `Map` keeps insertion order, which is exactly the first-occurrence order wanted. */
  const rows = new Map<string, StepRow>();
  for (const step of steps) {
    const label = stepLabel(t, step);
    const key = `${step.state}\u0000${label}`;
    const row = rows.get(key);
    rows.set(
      key,
      row === undefined
        ? { id: step.id, state: step.state, label, count: 1 }
        : { ...row, count: row.count + 1 },
    );
  }
  return [...rows.values()];
}

/**
 * What a tool row says, in two states each.
 *
 * ⚠️ **One `t()` per branch with a LITERAL key**, because `i18n:check` reads literal arguments only:
 * `t(\`agent.tool.\${name}.running\`)` would make every one of these entries report as an orphan, and
 * the fix somebody reaches for is deleting them.
 *
 * ⚠️ **An unknown tool still says something.** `screen-state.tsx`'s rule applies here too — a row
 * that renders nothing for a name this build does not know is the original bug in different clothes,
 * and a version skew between the API and the bundle is exactly when it would happen.
 */
function stepLabel(t: Translate, step: ThreadStep): string {
  /* ⚠️ **`one_at_a_time` is not a failure and must not read like one.** The server refused a SECOND
     proposal in the same turn because one is already waiting — telling somebody *"that read did not
     work"* would send them looking for a problem that is a rule. */
  if (step.state === 'failed') {
    return step.errorCode === 'one_at_a_time' ? t('agent.tool.notRun') : t('agent.tool.failed');
  }

  const running = step.state === 'running';
  if (step.name === 'tasks_search') {
    return running
      ? t('agent.tool.tasks_search.running')
      : t('agent.tool.tasks_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'tasks_get') {
    return running ? t('agent.tool.tasks_get.running') : t('agent.tool.tasks_get.done');
  }
  /* ⚠️ C49.1 · *"mi történt a HSO-131-tel"*. No count on the settled row: the tool returns TWO
     lists — the comments and the log — so one number would be a figure about neither. */
  if (step.name === 'tasks_activity') {
    return running ? t('agent.tool.tasks_activity.running') : t('agent.tool.tasks_activity.done');
  }
  if (step.name === 'worklogs_search') {
    return running
      ? t('agent.tool.worklogs_search.running')
      : t('agent.tool.worklogs_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'worklogs_overview') {
    return running
      ? t('agent.tool.worklogs_overview.running')
      : t('agent.tool.worklogs_overview.done');
  }
  /* ⚠️ **A write tool's *done* row does NOT say the write happened.** It proposed one, and the card
     underneath is where the decision is — *"Megerősítésre vár"* is the true sentence. */
  if (step.name === 'worklogs_log') {
    return running ? t('agent.tool.worklogs_log.running') : t('agent.tool.worklogs_log.done');
  }
  if (step.name === 'tasks_update') {
    return running ? t('agent.tool.tasks_update.running') : t('agent.tool.tasks_update.done');
  }
  if (step.name === 'tasks_create') {
    return running ? t('agent.tool.tasks_create.running') : t('agent.tool.tasks_create.done');
  }
  if (step.name === 'worklogs_update') {
    return running ? t('agent.tool.worklogs_update.running') : t('agent.tool.worklogs_update.done');
  }
  /* ⚠️ C49 · R-36's two. Their *done* row says the same *"waiting for you"* as every other write —
     the row above a delete card must not read as though anything has gone. */
  if (step.name === 'worklogs_delete') {
    return running ? t('agent.tool.worklogs_delete.running') : t('agent.tool.worklogs_delete.done');
  }
  if (step.name === 'tasks_delete') {
    return running ? t('agent.tool.tasks_delete.running') : t('agent.tool.tasks_delete.done');
  }
  /* ⚠️ C27.4's three lookups. They answer *"which project is that?"* rather than drawing a table, so
     their settled state is a count rather than a noun somebody would look for on screen. */
  if (step.name === 'projects_search') {
    return running
      ? t('agent.tool.projects_search.running')
      : t('agent.tool.projects_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'projects_get') {
    return running ? t('agent.tool.projects_get.running') : t('agent.tool.projects_get.done');
  }
  if (step.name === 'people_search') {
    return running
      ? t('agent.tool.people_search.running')
      : t('agent.tool.people_search.done', { count: step.count ?? 0 });
  }
  /* ⚠️ C44 · the contract spine's eight, in the registry's own order. Literal keys, per the header:
     `t(\`agent.tool.\${name}.running\`)` would make every entry in this block report as an orphan and
     the fix somebody reaches for is deleting them. */
  if (step.name === 'contracts_search') {
    return running
      ? t('agent.tool.contracts_search.running')
      : t('agent.tool.contracts_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'contracts_get') {
    return running ? t('agent.tool.contracts_get.running') : t('agent.tool.contracts_get.done');
  }
  if (step.name === 'line_items_search') {
    return running
      ? t('agent.tool.line_items_search.running')
      : t('agent.tool.line_items_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'contracts_worklogs') {
    return running
      ? t('agent.tool.contracts_worklogs.running')
      : t('agent.tool.contracts_worklogs.done', { count: step.count ?? 0 });
  }
  if (step.name === 'contracts_create') {
    return running
      ? t('agent.tool.contracts_create.running')
      : t('agent.tool.contracts_create.done');
  }
  if (step.name === 'contracts_update') {
    return running
      ? t('agent.tool.contracts_update.running')
      : t('agent.tool.contracts_update.done');
  }
  if (step.name === 'line_items_create') {
    return running
      ? t('agent.tool.line_items_create.running')
      : t('agent.tool.line_items_create.done');
  }
  if (step.name === 'line_items_update') {
    return running
      ? t('agent.tool.line_items_update.running')
      : t('agent.tool.line_items_update.done');
  }
  /* ⚠️ Phase 3 and 4 · the twenty-one tools over leave, overtime, the calendar, allocations, money
     and the report builder, in the registry's own order. Literal keys, per the header. A write's
     *done* row says *waiting for you*, like every other write's. */
  if (step.name === 'leave_requests_search') {
    return running
      ? t('agent.tool.leave_requests_search.running')
      : t('agent.tool.leave_requests_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'leave_requests_get') {
    return running
      ? t('agent.tool.leave_requests_get.running')
      : t('agent.tool.leave_requests_get.done');
  }
  if (step.name === 'leave_balances') {
    return running ? t('agent.tool.leave_balances.running') : t('agent.tool.leave_balances.done');
  }
  if (step.name === 'leave_requests_decide') {
    return running
      ? t('agent.tool.leave_requests_decide.running')
      : t('agent.tool.leave_requests_decide.done');
  }
  if (step.name === 'overtime_requests_search') {
    return running
      ? t('agent.tool.overtime_requests_search.running')
      : t('agent.tool.overtime_requests_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'overtime_requests_decide') {
    return running
      ? t('agent.tool.overtime_requests_decide.running')
      : t('agent.tool.overtime_requests_decide.done');
  }
  if (step.name === 'holidays_search') {
    return running
      ? t('agent.tool.holidays_search.running')
      : t('agent.tool.holidays_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'schedules_search') {
    return running
      ? t('agent.tool.schedules_search.running')
      : t('agent.tool.schedules_search.done');
  }
  if (step.name === 'allocations_search') {
    return running
      ? t('agent.tool.allocations_search.running')
      : t('agent.tool.allocations_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'allocations_create') {
    return running
      ? t('agent.tool.allocations_create.running')
      : t('agent.tool.allocations_create.done');
  }
  if (step.name === 'invoices_search') {
    return running
      ? t('agent.tool.invoices_search.running')
      : t('agent.tool.invoices_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'invoices_create') {
    return running ? t('agent.tool.invoices_create.running') : t('agent.tool.invoices_create.done');
  }
  if (step.name === 'invoices_set_status') {
    return running
      ? t('agent.tool.invoices_set_status.running')
      : t('agent.tool.invoices_set_status.done');
  }
  if (step.name === 'external_costs_search') {
    return running
      ? t('agent.tool.external_costs_search.running')
      : t('agent.tool.external_costs_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'external_costs_create') {
    return running
      ? t('agent.tool.external_costs_create.running')
      : t('agent.tool.external_costs_create.done');
  }
  if (step.name === 'contracts_finance') {
    return running
      ? t('agent.tool.contracts_finance.running')
      : t('agent.tool.contracts_finance.done');
  }
  if (step.name === 'rate_cards_search') {
    return running
      ? t('agent.tool.rate_cards_search.running')
      : t('agent.tool.rate_cards_search.done');
  }
  if (step.name === 'reports_run') {
    return running
      ? t('agent.tool.reports_run.running')
      : t('agent.tool.reports_run.done', { count: step.count ?? 0 });
  }
  if (step.name === 'reports_search') {
    return running
      ? t('agent.tool.reports_search.running')
      : t('agent.tool.reports_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'reports_get') {
    return running ? t('agent.tool.reports_get.running') : t('agent.tool.reports_get.done');
  }
  if (step.name === 'reports_create') {
    return running ? t('agent.tool.reports_create.running') : t('agent.tool.reports_create.done');
  }
  /* ⚠️ R-46 · clients, projects and project members, last in the registry as they are here. */
  if (step.name === 'clients_search') {
    return running
      ? t('agent.tool.clients_search.running')
      : t('agent.tool.clients_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'project_members_search') {
    return running
      ? t('agent.tool.project_members_search.running')
      : t('agent.tool.project_members_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'projects_create') {
    return running ? t('agent.tool.projects_create.running') : t('agent.tool.projects_create.done');
  }
  if (step.name === 'projects_update') {
    return running ? t('agent.tool.projects_update.running') : t('agent.tool.projects_update.done');
  }
  if (step.name === 'project_members_add') {
    return running
      ? t('agent.tool.project_members_add.running')
      : t('agent.tool.project_members_add.done');
  }
  /* ⚠️ The ready-to-build set — thirteen proposals, so every done row says *waiting for you*. */
  if (step.name === 'task_comments_add') {
    return running
      ? t('agent.tool.task_comments_add.running')
      : t('agent.tool.task_comments_add.done');
  }
  if (step.name === 'task_comments_update') {
    return running
      ? t('agent.tool.task_comments_update.running')
      : t('agent.tool.task_comments_update.done');
  }
  if (step.name === 'task_links_add') {
    return running ? t('agent.tool.task_links_add.running') : t('agent.tool.task_links_add.done');
  }
  if (step.name === 'task_dependencies_add') {
    return running
      ? t('agent.tool.task_dependencies_add.running')
      : t('agent.tool.task_dependencies_add.done');
  }
  if (step.name === 'allocations_update') {
    return running
      ? t('agent.tool.allocations_update.running')
      : t('agent.tool.allocations_update.done');
  }
  if (step.name === 'external_costs_update') {
    return running
      ? t('agent.tool.external_costs_update.running')
      : t('agent.tool.external_costs_update.done');
  }
  if (step.name === 'overtime_requests_raise') {
    return running
      ? t('agent.tool.overtime_requests_raise.running')
      : t('agent.tool.overtime_requests_raise.done');
  }
  if (step.name === 'reports_update') {
    return running ? t('agent.tool.reports_update.running') : t('agent.tool.reports_update.done');
  }
  if (step.name === 'reports_duplicate') {
    return running
      ? t('agent.tool.reports_duplicate.running')
      : t('agent.tool.reports_duplicate.done');
  }
  if (step.name === 'reports_refresh') {
    return running ? t('agent.tool.reports_refresh.running') : t('agent.tool.reports_refresh.done');
  }
  if (step.name === 'clients_create') {
    return running ? t('agent.tool.clients_create.running') : t('agent.tool.clients_create.done');
  }
  if (step.name === 'clients_update') {
    return running ? t('agent.tool.clients_update.running') : t('agent.tool.clients_update.done');
  }
  if (step.name === 'project_members_update') {
    return running
      ? t('agent.tool.project_members_update.running')
      : t('agent.tool.project_members_update.done');
  }
  /* ⚠️ R-47's deletes and the relations read, R-48's leave request. */
  if (step.name === 'task_relations_search') {
    return running
      ? t('agent.tool.task_relations_search.running')
      : t('agent.tool.task_relations_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'task_links_delete') {
    return running
      ? t('agent.tool.task_links_delete.running')
      : t('agent.tool.task_links_delete.done');
  }
  if (step.name === 'task_dependencies_delete') {
    return running
      ? t('agent.tool.task_dependencies_delete.running')
      : t('agent.tool.task_dependencies_delete.done');
  }
  if (step.name === 'task_comments_delete') {
    return running
      ? t('agent.tool.task_comments_delete.running')
      : t('agent.tool.task_comments_delete.done');
  }
  if (step.name === 'allocations_delete') {
    return running
      ? t('agent.tool.allocations_delete.running')
      : t('agent.tool.allocations_delete.done');
  }
  if (step.name === 'external_costs_delete') {
    return running
      ? t('agent.tool.external_costs_delete.running')
      : t('agent.tool.external_costs_delete.done');
  }
  if (step.name === 'reports_delete') {
    return running ? t('agent.tool.reports_delete.running') : t('agent.tool.reports_delete.done');
  }
  if (step.name === 'project_members_delete') {
    return running
      ? t('agent.tool.project_members_delete.running')
      : t('agent.tool.project_members_delete.done');
  }
  if (step.name === 'leave_requests_create') {
    return running
      ? t('agent.tool.leave_requests_create.running')
      : t('agent.tool.leave_requests_create.done');
  }
  /* D10 · the deal tools. */
  if (step.name === 'deals_search') {
    return running
      ? t('agent.tool.deals_search.running')
      : t('agent.tool.deals_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'deals_get') {
    return running ? t('agent.tool.deals_get.running') : t('agent.tool.deals_get.done');
  }
  if (step.name === 'deals_pipeline') {
    return running ? t('agent.tool.deals_pipeline.running') : t('agent.tool.deals_pipeline.done');
  }
  if (step.name === 'deals_forecast') {
    return running ? t('agent.tool.deals_forecast.running') : t('agent.tool.deals_forecast.done');
  }
  if (step.name === 'deals_demand') {
    return running ? t('agent.tool.deals_demand.running') : t('agent.tool.deals_demand.done');
  }
  if (step.name === 'deal_settings_get') {
    return running
      ? t('agent.tool.deal_settings_get.running')
      : t('agent.tool.deal_settings_get.done');
  }
  if (step.name === 'deals_create') {
    return running ? t('agent.tool.deals_create.running') : t('agent.tool.deals_create.done');
  }
  if (step.name === 'deals_update') {
    return running ? t('agent.tool.deals_update.running') : t('agent.tool.deals_update.done');
  }
  if (step.name === 'deals_set_status') {
    return running
      ? t('agent.tool.deals_set_status.running')
      : t('agent.tool.deals_set_status.done');
  }
  if (step.name === 'deals_mark_won') {
    return running ? t('agent.tool.deals_mark_won.running') : t('agent.tool.deals_mark_won.done');
  }
  if (step.name === 'deals_mark_lost') {
    return running ? t('agent.tool.deals_mark_lost.running') : t('agent.tool.deals_mark_lost.done');
  }
  if (step.name === 'deals_set_next_step') {
    return running
      ? t('agent.tool.deals_set_next_step.running')
      : t('agent.tool.deals_set_next_step.done');
  }
  if (step.name === 'deals_log_activity') {
    return running
      ? t('agent.tool.deals_log_activity.running')
      : t('agent.tool.deals_log_activity.done');
  }
  if (step.name === 'deals_reopen') {
    return running ? t('agent.tool.deals_reopen.running') : t('agent.tool.deals_reopen.done');
  }
  if (step.name === 'deals_complete_next_step') {
    return running
      ? t('agent.tool.deals_complete_next_step.running')
      : t('agent.tool.deals_complete_next_step.done');
  }
  if (step.name === 'deals_update_activity') {
    return running
      ? t('agent.tool.deals_update_activity.running')
      : t('agent.tool.deals_update_activity.done');
  }
  if (step.name === 'deals_delete_activity') {
    return running
      ? t('agent.tool.deals_delete_activity.running')
      : t('agent.tool.deals_delete_activity.done');
  }
  if (step.name === 'deals_delete') {
    return running ? t('agent.tool.deals_delete.running') : t('agent.tool.deals_delete.done');
  }
  if (step.name === 'deal_scenarios_search') {
    return running
      ? t('agent.tool.deal_scenarios_search.running')
      : t('agent.tool.deal_scenarios_search.done', { count: step.count ?? 0 });
  }
  if (step.name === 'deal_scenarios_get') {
    return running
      ? t('agent.tool.deal_scenarios_get.running')
      : t('agent.tool.deal_scenarios_get.done');
  }
  if (step.name === 'deal_scenarios_mark_winner') {
    return running
      ? t('agent.tool.deal_scenarios_mark_winner.running')
      : t('agent.tool.deal_scenarios_mark_winner.done');
  }
  return running ? t('agent.tool.unknown.running') : t('agent.tool.unknown.done');
}

/**
 * The summarised reasoning, collapsed.
 *
 * ⚠️⚠️ **THE MOCKUP DRAWS NOTHING FOR THIS, AND IT IS AN ADDITION FLAGGED PER RULE 8.** Its agent
 * has no thinking to show — it matched keywords against a table. The design system decides what the
 * mockup does not draw, and it has a disclosure: `<details>`, native, keyboard-operable, and
 * announced without an `aria-expanded` we would have to maintain.
 *
 * ⚠️ **Collapsed by default, and that is the whole point of showing it at all.** Reasoning is
 * evidence somebody can go and check when an answer surprises them; it is not the answer. Expanded
 * by default it would bury every reply under a paragraph nobody asked for.
 *
 * ⚠️ **A SUMMARY, and it is labelled as one rather than presented as the model's inner monologue.**
 * The API never returns a raw chain of thought on any model — what arrives is a summary, and calling
 * it anything else would be a claim about the product that is not true.
 */
function Reasoning({ text }: { readonly text: string }): ReactNode {
  const t = useT();

  return (
    <details className="group mb-2">
      <summary className="text-muted-foreground hover:text-foreground marker:content-none flex cursor-pointer list-none items-center gap-1.5 text-[11.5px] select-none">
        <Icon
          name="chevron-right"
          size={12}
          className="transition-transform group-open:rotate-90"
          aria-hidden
        />
        <span className="group-open:hidden">{t('agent.thinkingShow')}</span>
        <span className="hidden group-open:inline">{t('agent.thinkingHide')}</span>
      </summary>
      {/* ⚠️ Through the same renderer as the reply (C48), because this is the model's text as well
          and *"the thinking arrived with asterisks in it"* is the same report. A `div`, not a `p`:
          the renderer emits blocks. */}
      <div className="text-muted-foreground border-border mt-1.5 border-l-2 pl-3 text-[12px] leading-relaxed">
        <AgentMarkdown text={text} />
      </div>
    </details>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C53 · THE CLIENTS' OWN MARKS, WHICH SAY THE THING THE LABEL CANNOT.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * *Saját agent beállítása* is accurate and tells nobody whether **their** agent is one of the ones
 * this works with. Four marks answer that before the sentence is read, which is the whole of why
 * Cloudflare's version of this button carries them. The product owner supplied the artwork on
 * 16 Sep 2026, as they did Jira's and YouTrack's on 9 Sep (D9).
 *
 * ⚠️ **NOT `Icon` NAMES, AND NOT `ProviderLogo` EITHER.** `icon.tsx` is one stroke weight in
 * `currentColor`, which a four-colour Gemini mark is not — that is `provider-logo.tsx`'s argument
 * and it applies here unchanged. But that component's map is the *integrations* catalogue, keyed by
 * tracker id: putting Claude in it would make `hasLogo('openai')` answer for a provider the
 * integrations screen has never heard of. Same technique, different list.
 *
 * ⚠️⚠️ **ONE OF THE FOUR IS PURE BLACK AND WOULD VANISH ON SIX OF THE EIGHT THEMES.** OpenAI's mark
 * is a single `#000000` fill; `dark` is a class on `<html>` for every dark palette, so `dark:invert`
 * turns it white and touches nothing else — the file has exactly one fill, so there is no colour for
 * the filter to corrupt. The other three carry their own hue (`#D97757`, `#3F7E8B`, and Google's
 * four) and are legible on both, so they are left alone. ⚠️ A blanket `invert` on all four would
 * make Claude's orange a pale blue, which is a vendor's mark drawn wrong.
 *
 * ⚠️ **Decorative, and `aria-hidden` on the wrapper with an empty `alt` on each.** The button's own
 * label is its accessible name; four vendors read out before it would be noise. `provider-logo.tsx`
 * records why the empty `alt` is there as well as the attribute.
 */
const CLIENT_MARKS: readonly {
  readonly id: string;
  readonly src: string;
  readonly dark: boolean;
}[] = [
  { id: 'claude', src: claudeMark, dark: false },
  { id: 'openai', src: openaiMark, dark: true },
  { id: 'perplexity', src: perplexityMark, dark: false },
  { id: 'gemini', src: geminiMark, dark: false },
];

function ClientMarks(): ReactNode {
  return (
    <span className="flex items-center gap-1" aria-hidden>
      {CLIENT_MARKS.map((mark) => (
        <img
          key={mark.id}
          data-mark={mark.id}
          src={mark.src}
          alt=""
          width={14}
          height={14}
          /* ⚠️ The literal string matters: Tailwind scans source text, so a class assembled from
             parts would never be generated. */
          className={mark.dark ? 'dark:invert' : undefined}
          style={{ width: 14, height: 14 }}
        />
      ))}
    </span>
  );
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ C53 · THE ONLY THING THIS BUTTON PUTS ON THE CLIPBOARD IS ONE        │
 * │ SENTENCE, AND THE SENTENCE IS ADDRESSED TO A MACHINE.                     │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Somebody pastes it into Claude Code, Claude Desktop or whatever else they run; that agent fetches
 * `/agent-setup/prompt.md` from this deployment and the document takes it from there. The pattern is
 * Cloudflare's, and the reason to copy a POINTER rather than the instructions themselves is that a
 * hundred lines of setup pasted into a chat is unreadable, unversioned, and stale the moment the
 * document changes.
 *
 * ⚠️⚠️ **THE ORIGIN COMES FROM THE BROWSER, WHICH IS THE ONLY PLACE IT IS KNOWN FOR CERTAIN.** This
 * product is self-hosted, so there is no one address to bake in: `oauth-config.ts` derives every
 * OAuth URL from `APP_ORIGIN` for exactly this reason, and the bundle deliberately contains no
 * origin at all (R-22, and `apps/web/Dockerfile` argues it at length). `window.location.origin` is
 * the hostname the person actually reached, including a port and the real scheme — which
 * `$scheme` in nginx would get wrong behind a TLS-terminating proxy.
 *
 * ⚠️ **And the document itself needs no origin**, because it tells the agent to take it from the URL
 * it just fetched. That is what keeps this a static file rather than something that has to be
 * rendered per deployment.
 *
 * ⚠️ **A copy can fail** — a denied permission, an insecure context, a browser with no Clipboard
 * API — and unlike the token dialog there is nothing on screen to fall back to. That is a smaller
 * loss than it looks: every case where `writeText` is unavailable is a non-https origin, and OAuth
 * 2.1 refuses a non-loopback redirect over http, so the setup this button starts could not have
 * finished on such an origin anyway. It says so rather than pretending.
 */
const SETUP_PROMPT_PATH = '/agent-setup/prompt.md';

function AgentSetup(): ReactNode {
  const t = useT();
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const mobile = useIsMobile();

  /* ⚠️ Back to the label after a few seconds. The header is permanent, so a button that says
   *Kimásolva* forever is a button whose text stops describing what it does. */
  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => {
      setState('idle');
    }, 4000);
    return () => {
      clearTimeout(timer);
    };
  }, [state]);

  /* ⚠️ **The marks are the IDLE state only.** They are there to say *this works with your agent*,
     which is a question already answered by the time somebody has clicked; after that the slot is
     worth more as the outcome. */
  const glyph =
    state === 'idle' ? (
      /* 19 Oct 2026 · one plug on a phone — four marks and the words pushed the header off screen. */
      mobile ? (
        <Icon name="plug" size={14} />
      ) : (
        <ClientMarks />
      )
    ) : (
      <Icon name={state === 'copied' ? 'check' : 'triangle-alert'} size={14} />
    );

  /* ⚠️ One `t()` call per branch with a literal key — `i18n/index.tsx` says why a computed key is
     invisible to the catalogue check. */
  const label =
    state === 'copied'
      ? t('agent.setup.copied')
      : state === 'failed'
        ? t('agent.setup.failed')
        : t('agent.setup.label');

  return (
    <>
      <Button
        variant="ghost"
        size={mobile ? 'icon-sm' : 'sm'}
        aria-label={label}
        title={label}
        onClick={() => {
          /* ⚠️ Widened rather than optional-chained on the property: `navigator.clipboard` is typed as
           always present and is absent in jsdom and on insecure origins. `integrations-section.tsx`
           reaches it the same way, for the same reason. */
          const clipboard = (
            navigator as unknown as {
              clipboard?: { writeText?: (text: string) => Promise<void> };
            }
          ).clipboard;
          const written = clipboard?.writeText?.(
            t('agent.setup.instruction', { url: `${window.location.origin}${SETUP_PROMPT_PATH}` }),
          );
          if (written === undefined) {
            setState('failed');
            return;
          }
          void written
            .then(() => {
              setState('copied');
            })
            .catch(() => {
              setState('failed');
            });
        }}
      >
        {glyph}
        {mobile ? null : label}
      </Button>
      {/* 19 Oct 2026 · on a phone the button is an icon with no words to change, so the outcome
        is said in a toast instead — and it says what to do with what was copied. */}
      {mobile && (
        <Toaster
          toasts={
            state === 'idle'
              ? []
              : [
                  state === 'copied'
                    ? {
                        id: 'agent-setup',
                        title: t('agent.setup.copiedToast'),
                        description: t('agent.setup.copiedHint'),
                        variant: 'success',
                      }
                    : { id: 'agent-setup', title: t('agent.setup.failed'), variant: 'error' },
                ]
          }
          label={t('agent.toasts')}
          dismissLabel={t('common.close')}
          onDismiss={() => {
            setState('idle');
          }}
        />
      )}
    </>
  );
}

/**
 * The mockup's two header controls (`markup.html` 261–270), plus C53's third.
 *
 * ⚠️ **One dropdown and one button, in that order**, with the mockup's own icons — `clock` for the
 * history and `plus` for a new thread — and its own variants: `ghost` for the menu trigger, `outline`
 * for the button.
 *
 * ⚠️⚠️ **THE THIRD CONTROL IS NOT THE MOCKUP'S, AND IT GOES FIRST.** *Saját agent beállítása* is an
 * addition (rule 8): the mockup predates the MCP server and draws nothing for it. It leads the row
 * because it is the one control that is about something OTHER than this conversation — leaving and
 * setting up a different tool — and putting it after *Új beszélgetés* would read as a third way to
 * do the same thing.
 */
function History({
  conversations,
  loading,
  openId,
  mcpOn,
  onOpen,
  onNew,
  onDelete,
  onRename,
}: {
  readonly conversations: readonly AgentConversation[];
  /** Until the first read settles — one disabled loading row instead of the empty one. */
  readonly loading: boolean;
  readonly openId: string | null;
  /** Whether `mcp.access` is on — see the note at the `usePageHeader` call. */
  readonly mcpOn: boolean;
  readonly onOpen: (id: string) => void;
  readonly onNew: () => void;
  readonly onDelete: () => void;
  readonly onRename: (title: string) => void;
}): ReactNode {
  const mobile = useIsMobile();
  const t = useT();
  const { locale } = useI18n();

  /**
   * ⚠️ **An untitled thread is named by the CATALOGUE, not by the database.** 0042 refused a
   * `default 'Új beszélgetés'` on the column because that would be a user-facing string in Postgres;
   * the absence travels and this is where it gets a name.
   */
  const open = conversations.find((conversation) => conversation.id === openId);
  const items: MenuItem[] = [
    { type: 'label', label: t('agent.history.label') },
    ...(conversations.length === 0
      ? [{ label: t(loading ? 'state.loading' : 'agent.history.empty'), disabled: true }]
      : conversations.map((conversation) => ({
          label: conversation.title ?? t('agent.untitled'),
          /* ⚠️ The day it was last used, in the reader's locale — two threads about the same thing
             are otherwise indistinguishable in a list ordered by a date nobody can see. */
          hint: dayLabel(locale, conversation.updatedAt),
          /* ⚠️ A check on the open one: a menu that cannot say where you are makes somebody click
             the thread they are already reading to find out. */
          checked: conversation.id === openId ? true : undefined,
          onSelect: () => {
            onOpen(conversation.id);
          },
        }))),
    { type: 'separator' },
    /**
     * ┌──────────────────────────────────────────────────────────────────────┐
     * │ 29 Oct 2026 · RENAME, ABOVE DELETE. Chosen, the row becomes a field. │
     * └──────────────────────────────────────────────────────────────────────┘
     *
     * ⚠️ **Disabled with nothing open**, for delete's reason, **and on an MCP channel**: its title is
     * the client's own name and the key the client finds it by (`0122`), so renaming it would
     * abandon it. The server refuses that too (`conversation_not_renamable`).
     *
     * ⚠️ **120, the column's own limit (`0042`)**, so the field cannot hold a name the server would
     * refuse.
     */
    {
      label: t('agent.history.rename'),
      icon: 'pencil',
      disabled: open === undefined || open.origin === 'mcp',
      edit:
        open === undefined
          ? undefined
          : {
              value: open.title ?? '',
              fieldLabel: t('agent.history.renameField'),
              saveLabel: t('agent.history.renameSave'),
              maxLength: 120,
              onSave: onRename,
            },
    },
    {
      label: t('agent.history.delete'),
      icon: 'trash',
      destructive: true,
      /* ⚠️ **Disabled with nothing open**, because a thread that has not been created cannot be
         deleted — the conversation is created lazily, on the first message. */
      disabled: openId === null,
      onSelect: onDelete,
    },
  ];

  return (
    <div className="flex items-center gap-2">
      {mcpOn && <AgentSetup />}

      <DropdownMenu
        items={items}
        width={280}
        align="end"
        label={t('agent.history.label')}
        /* ⚠️ A filter once a member has more threads than a menu can show at a glance. The cap is the
           server's hundred; this is what makes the hundredth reachable. */
        searchPlaceholder={conversations.length > 8 ? t('agent.history.search') : undefined}
        emptyLabel={t('agent.history.empty')}
      >
        {/* 17 Oct 2026 · icons only on a phone, where the words pushed the header off screen. */}
        <Button
          variant="ghost"
          size={mobile ? 'icon-sm' : 'sm'}
          data-tour="agent-history"
          aria-label={t('agent.history.label')}
          title={t('agent.history.label')}
        >
          <Icon name="clock" size={14} />
          {mobile ? null : t('agent.history.label')}
        </Button>
      </DropdownMenu>

      <Button
        variant="outline"
        size={mobile ? 'icon-sm' : 'sm'}
        onClick={onNew}
        aria-label={t('agent.history.new')}
        title={t('agent.history.new')}
      >
        <Icon name="plus" size={14} />
        {mobile ? null : t('agent.history.new')}
      </Button>
    </div>
  );
}

/**
 * A conversation's day, in the reader's locale.
 *
 * ⚠️ **An instant, formatted as a day** — `updated_at` is a `timestamptz`, so this is the one place in
 * this file that is allowed to call a date formatter on it. A `workedOn` is a calendar day and would
 * be wrong here; `hard-won.md`'s driver rows are about not confusing the two.
 */
function dayLabel(locale: 'hu' | 'en', at: string): string {
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(locale === 'hu' ? 'hu-HU' : 'en-GB', {
    month: 'short',
    day: 'numeric',
  });
}
